/**
 * BillingCatalog — Admin Catalog Entries CRUD UI
 *
 * @see US3, AC-11, AC-12 in docs/specifications/2026-03-08-billing-admin-ui.md
 */
import React, { useState, useEffect } from "react";
import Layout from "../../../components/Layout";
import { EmptyState } from "../../../components/common/EmptyState";
import { useBillingAdminBrand } from "../../../hooks/useBillingAdminBrand";
import { useProductsQuery } from "../../../hooks/useAdminProductCatalog";
import {
  useCatalogEntriesQuery,
  useCreateCatalogEntryMutation,
  useUpdateCatalogEntryMutation,
  useDeleteCatalogEntryMutation,
} from "../../../hooks/useAdminProductCatalog";
import BillingBrandGuard from "./BillingBrandGuard";
import BillingBrandSelector from "./BillingBrandSelector";
import type {
  CatalogEntry,
  CreateCatalogEntryDto,
  UpdateCatalogEntryDto,
} from "../../../types/adminBilling";

const PAGE_LIMIT = 20;

// =============================================================================
// HELPERS
// =============================================================================

const isExpired = (validTo?: string | null): boolean => {
  if (!validTo) return false;
  return new Date(validTo) < new Date();
};

const formatDate = (date?: string | null): string => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("pl-PL");
};

// =============================================================================
// TAG INPUT
// =============================================================================

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
}

const TagInput: React.FC<TagInputProps> = ({ value, onChange, disabled }) => {
  const [inputValue, setInputValue] = useState("");

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInputValue("");
  };

  const removeTag = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      removeTag(value.length - 1);
    }
  };

  return (
    <div
      className={`flex flex-wrap gap-1.5 items-center border rounded-md px-2 py-1.5 min-h-[38px] ${
        disabled ? "bg-neutral-50" : "bg-white"
      } border-neutral-300`}
    >
      {value.map((tag, index) => (
        <span
          key={index}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
        >
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={() => removeTag(index)}
              className="text-primary-700 hover:text-primary-800"
            >
              ×
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => inputValue && addTag(inputValue)}
          placeholder={value.length === 0 ? "Dodaj kategorię (Enter)..." : ""}
          className="flex-1 min-w-24 text-sm outline-none bg-transparent"
        />
      )}
    </div>
  );
};

// =============================================================================
// CATALOG ENTRY FORM MODAL
// =============================================================================

interface CatalogEntryFormModalProps {
  entry?: CatalogEntry | null;
  brandId: string;
  onClose: () => void;
}

const CatalogEntryFormModal: React.FC<CatalogEntryFormModalProps> = ({
  entry,
  brandId,
  onClose,
}) => {
  const isEditing = !!entry;

  // Load active products for the selector
  const { data: productsData } = useProductsQuery(brandId);
  const activeProducts =
    productsData?.data.filter((p) => p.status === "ACTIVE") ?? [];

  const [productId, setProductId] = useState(entry?.productId ?? "");
  const [displayName, setDisplayName] = useState(entry?.displayName ?? "");
  const [marketingDescription, setMarketingDescription] = useState(
    entry?.marketingDescription ?? ""
  );
  const [categories, setCategories] = useState<string[]>(
    entry?.categories ?? []
  );
  const [isVisible, setIsVisible] = useState(entry?.isVisible ?? true);
  const [validFrom, setValidFrom] = useState(
    entry?.validFrom
      ? new Date(entry.validFrom).toISOString().split("T")[0]
      : ""
  );
  const [validTo, setValidTo] = useState(
    entry?.validTo ? new Date(entry.validTo).toISOString().split("T")[0] : ""
  );
  // L-10: sortOrder stored as number, not string
  const [sortOrder, setSortOrder] = useState<number>(
    entry?.sortOrder ?? 0
  );
  const [metadataText, setMetadataText] = useState(
    entry?.metadata ? JSON.stringify(entry.metadata, null, 2) : "{}"
  );
  const [metadataError, setMetadataError] = useState<string | null>(null);

  const createMutation = useCreateCatalogEntryMutation();
  const updateMutation = useUpdateCatalogEntryMutation(entry?.id ?? "", brandId);
  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Escape-to-close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const validateMetadata = (text: string): boolean => {
    try {
      JSON.parse(text);
      setMetadataError(null);
      return true;
    } catch {
      setMetadataError("Nieprawidłowy JSON");
      return false;
    }
  };

  // L-16: Cross-validation — validFrom must be earlier than validTo
  const dateRangeError =
    validFrom && validTo && validFrom >= validTo
      ? "Data rozpoczęcia musi być wcześniejsza niż data zakończenia"
      : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    if (!isEditing && !productId) return;
    if (!validateMetadata(metadataText)) return;
    if (dateRangeError) return;

    const metadata = JSON.parse(metadataText);

    if (isEditing && entry) {
      const dto: UpdateCatalogEntryDto = {
        displayName: displayName.trim(),
        marketingDescription: marketingDescription.trim() || null,
        categories,
        metadata,
        isVisible,
        validFrom: validFrom || null,
        validTo: validTo || null,
        sortOrder,
      };
      updateMutation.mutate(dto, { onSuccess: onClose });
    } else {
      const dto: CreateCatalogEntryDto = {
        productId,
        brandId,
        displayName: displayName.trim(),
        marketingDescription: marketingDescription.trim() || null,
        categories,
        metadata,
        isVisible,
        validFrom: validFrom || null,
        validTo: validTo || null,
        sortOrder,
      };
      createMutation.mutate(dto, { onSuccess: onClose });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-900">
            {isEditing ? "Edytuj wpis katalogowy" : "Nowy wpis katalogowy"}
          </h3>
          <button
            onClick={onClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!isEditing && (
            <div>
              <label htmlFor="catalog-product" className="block text-sm font-medium text-neutral-700 mb-1">
                Produkt (tylko ACTIVE) *
              </label>
              <select
                id="catalog-product"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                required
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Wybierz produkt...</option>
                {activeProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.productType})
                  </option>
                ))}
              </select>
              {activeProducts.length === 0 && (
                <p className="text-amber-600 text-xs mt-1">
                  Brak aktywnych produktów. Aktywuj produkt najpierw.
                </p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="catalog-display-name" className="block text-sm font-medium text-neutral-700 mb-1">
              Nazwa wyświetlana *
            </label>
            <input
              id="catalog-display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              maxLength={255}
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Nazwa w cenniku"
            />
          </div>

          <div>
            <label htmlFor="catalog-description" className="block text-sm font-medium text-neutral-700 mb-1">
              Opis marketingowy
            </label>
            <textarea
              id="catalog-description"
              value={marketingDescription}
              onChange={(e) => setMarketingDescription(e.target.value)}
              rows={4}
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Opis dla użytkownika..."
            />
          </div>

          <div>
            <label htmlFor="catalog-categories" className="block text-sm font-medium text-neutral-700 mb-1">
              Kategorie
            </label>
            <TagInput
              value={categories}
              onChange={setCategories}
            />
            <p className="text-xs text-neutral-500 mt-1">
              Naciśnij Enter lub przecinek, aby dodać kategorię
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="catalog-valid-from" className="block text-sm font-medium text-neutral-700 mb-1">
                Ważny od
              </label>
              <input
                id="catalog-valid-from"
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  dateRangeError ? "border-red-300" : "border-neutral-300"
                }`}
              />
            </div>
            <div>
              <label htmlFor="catalog-valid-to" className="block text-sm font-medium text-neutral-700 mb-1">
                Ważny do
              </label>
              <input
                id="catalog-valid-to"
                type="date"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
                className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  dateRangeError ? "border-red-300" : "border-neutral-300"
                }`}
              />
            </div>
          </div>
          {/* L-16: Date range cross-validation error */}
          {dateRangeError && (
            <p className="text-red-600 text-xs -mt-2">{dateRangeError}</p>
          )}

          <div>
            <label htmlFor="catalog-sort-order" className="block text-sm font-medium text-neutral-700 mb-1">
              Kolejność (sort order)
            </label>
            {/* L-10: type="number", value is number, onChange converts to Number */}
            <input
              id="catalog-sort-order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={isVisible}
              onChange={(e) => setIsVisible(e.target.checked)}
              className="h-4 w-4 text-primary-600 border-neutral-300 rounded"
            />
            Widoczny w publicznym cenniku
          </label>

          <div>
            <label htmlFor="catalog-metadata" className="block text-sm font-medium text-neutral-700 mb-1">
              Metadane (JSON)
            </label>
            <textarea
              id="catalog-metadata"
              value={metadataText}
              onChange={(e) => {
                setMetadataText(e.target.value);
                validateMetadata(e.target.value);
              }}
              rows={4}
              className={`w-full border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                metadataError ? "border-red-300" : "border-neutral-300"
              }`}
              placeholder="{}"
            />
            {metadataError && (
              <p className="text-red-600 text-xs mt-1">{metadataError}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800 font-medium"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isLoading || !!dateRangeError}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isLoading ? "Zapisywanie..." : "Zapisz"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN PAGE
// =============================================================================

const BillingCatalog: React.FC = () => {
  const { selectedBrand, brands, isLoading, selectBrand, isBrandRequired } =
    useBillingAdminBrand();

  const [page, setPage] = useState(0);
  const offset = page * PAGE_LIMIT;

  const { data, isLoading: isLoadingEntries, isError: isErrorEntries, refetch: refetchEntries } = useCatalogEntriesQuery(
    selectedBrand?.id ?? null,
    { limit: PAGE_LIMIT, offset }
  );

  const deleteMutation = useDeleteCatalogEntryMutation(
    selectedBrand?.id ?? ""
  );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CatalogEntry | null>(null);

  const entries = data?.data ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? PAGE_LIMIT;

  // Reset page when brand changes
  useEffect(() => {
    setPage(0);
  }, [selectedBrand?.id]);

  const handleDelete = (entry: CatalogEntry) => {
    if (
      window.confirm(
        `Czy na pewno chcesz usunąć wpis "${entry.displayName}"?`
      )
    ) {
      deleteMutation.mutate(entry.id);
    }
  };

  return (
    <Layout>
      <BillingBrandGuard
        isBrandRequired={isBrandRequired}
        brands={brands}
        isLoading={isLoading}
        onSelectBrand={selectBrand}
      >
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                Katalog produktów
              </h1>
              <p className="text-neutral-600 mt-1 text-sm">
                Zarządzaj wpisami katalogowymi (warstwa marketingowa produktów)
              </p>
            </div>
            <div className="flex items-center gap-4">
              <BillingBrandSelector
                brands={brands}
                selectedBrand={selectedBrand}
                onSelectBrand={selectBrand}
              />
              {selectedBrand && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  + Nowy wpis
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          {isErrorEntries ? (
            <div className="p-6 text-center">
              <p className="text-red-600">Wystąpił błąd podczas ładowania danych.</p>
              <button onClick={() => refetchEntries()} className="mt-2 text-primary-700 underline">
                Spróbuj ponownie
              </button>
            </div>
          ) : isLoadingEntries ? (
            <div className="flex items-center justify-center min-h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : entries.length === 0 ? (
            <div className="bg-white rounded-lg border border-neutral-200">
              <EmptyState
                title="Brak wpisów katalogowych"
                description="Nie znaleziono wpisów katalogowych dla wybranego brandu."
                action={
                  selectedBrand
                    ? {
                        label: "Nowy wpis",
                        onClick: () => setShowCreateModal(true),
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Nazwa wyświetlana
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Produkt
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Kategorie
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Widoczność
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Ważność
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Sort
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Akcje
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {entries.map((entry) => {
                      const expired = isExpired(entry.validTo);
                      return (
                        <tr key={entry.id} className="hover:bg-neutral-50">
                          <td className="px-6 py-4">
                            <div className="font-medium text-neutral-900">
                              {entry.displayName}
                            </div>
                            {entry.marketingDescription && (
                              <div className="text-sm text-neutral-500 truncate max-w-xs">
                                {entry.marketingDescription}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                            {entry.product?.name ?? entry.productId.slice(0, 8)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {(entry.categories ?? []).map((cat) => (
                                <span
                                  key={cat}
                                  className="px-1.5 py-0.5 rounded text-xs bg-neutral-100 text-neutral-600"
                                >
                                  {cat}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {!entry.isVisible ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-500">
                                Ukryty
                              </span>
                            ) : expired ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                Wygasły
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Widoczny
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                            {formatDate(entry.validFrom)} —{" "}
                            {formatDate(entry.validTo)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                            {entry.sortOrder}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                            <button
                              onClick={() => setEditingEntry(entry)}
                              className="text-primary-600 hover:text-primary-800 font-medium"
                            >
                              Edytuj
                            </button>
                            <button
                              onClick={() => handleDelete(entry)}
                              disabled={deleteMutation.isPending}
                              className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                            >
                              Usuń
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {total > limit && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 text-sm text-gray-600">
                  <span>
                    Wyświetlanie {offset + 1}-{Math.min(offset + limit, total)} z {total} rekordów
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={offset === 0}
                      onClick={() => setPage((p) => p - 1)}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Poprzednia
                    </button>
                    <button
                      disabled={offset + limit >= total}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Następna
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </BillingBrandGuard>

      {/* Create Modal */}
      {showCreateModal && selectedBrand && (
        <CatalogEntryFormModal
          brandId={selectedBrand.id}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Edit Modal */}
      {editingEntry && selectedBrand && (
        <CatalogEntryFormModal
          entry={editingEntry}
          brandId={selectedBrand.id}
          onClose={() => setEditingEntry(null)}
        />
      )}
    </Layout>
  );
};

export default BillingCatalog;
